import React, { useState, useEffect } from 'react';
import CustomLoader from "../common/Loader";
import { Modal } from 'react-bootstrap';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import {
	getFormWidgetQuestionsAnswers,
	saveFormClientSideValidation,
	getFormClientSideValidation,
	getFormWidgets, getAvailableInstitutions,
} from '../../services/formService';
import { QuestionTypes } from '../../constants/QuestionTypes';
import { stripTags } from '../common/commonFuncs';
import { isNullOrEmpty } from '../common/commonFuncs';
import * as Icon from "react-feather";
import { toaster } from "../../config";
import {getListValues} from "../../services/listService";

function ClientSideValidation({ Project_ID, Form_ID }) {
	const [loading, setLoading] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [rules, setRules] = useState([]);

	// list of {Widget_ID, Question_ID, Question_Type_ID, Question_Text, Matrix_Questions (a list of questions excluding the first title question)}
	const [questionWidgets, setQuestionWidgets] = useState(null);

	// map with Question_ID as key, list of {Answer_ID, Answer_Text} as value
	const [questionAnswers, setQuestionAnswers] = useState(null);

	// list of all widgets in the form (excluding those in a row)
	const [widgets, setWidgets] = useState(null);

	// map of lists that are involved in a condition. Key: List_ID, value: list of values
	const [lists, setLists] = useState(null);

	// list of institutions
	const [institutions, setInstitutions] = useState(null);

	const defaultRule = {
		Widget_ID: 0,
		RuleAction: 'Show',
		Conditions: [],
		RuleOperator: 'And'
	};
	const defaultCondition = {
		Widget_ID: 0,
		Question_Type_ID: 0,
		Question_ID: null,
		Question_Text: '',
		Answer_ID: null,
		Answer_Text: '',
		Matrix_Question_ID: null,
		Matrix_Question_Type_ID: null,
		Matrix_Question_Text: null,
		ConditionOperator: 'Equals',
		List_ID: null,
		CompareWidget: false,
		Target_Widget_ID: 0,
		CheckIfRandomized: false,
		IsRandomized: -1
	};

	const handleOpenModal = async () => {
		setOpenModal(true);
		setLoading(true);

		let { data: rulesData} = await getFormClientSideValidation(Form_ID);
		rulesData.forEach( rule => rule.Conditions = JSON.parse(rule.Conditions))
		setRules(rulesData);

		let { data: widgetsData} = await getFormWidgets(Form_ID);
		//widgetsData = widgetsData.filter(w => !w.WithinRow);
		widgetsData.forEach(w => w.Question_Text ? w.Question_Text = stripTags(w.Question_Text) : null);
		setWidgets(widgetsData);

		let { data: institutionsData } = await getAvailableInstitutions(Project_ID);
		setInstitutions(institutionsData);

		let { data } = await getFormWidgetQuestionsAnswers(Form_ID);
		let updatedQuestionWidgets = [];
		let updatedQuestionAnswers = new Map();
		data.filter((w) => 
			[
				QuestionTypes.Text,
				QuestionTypes.Numeric,
				QuestionTypes.Multiple_Choice,
				QuestionTypes.Dropdown,
				QuestionTypes.Date,
				QuestionTypes.Time,
				QuestionTypes.Matrix,
				QuestionTypes.Numeric_Tally,
				QuestionTypes.Picklist,
				QuestionTypes.Scale,
				QuestionTypes.Identifier,
				QuestionTypes.Institution,
				QuestionTypes.Scale,
				QuestionTypes.Checkbox,
				QuestionTypes.Textarea
		  	].includes(w.Question_Type_ID)
		).map((w) => {
			if(!updatedQuestionWidgets.some( obj => obj.Widget_ID === w.Widget_ID)) {
				let questionWidget = null;
				if (w.Question_Type_ID === QuestionTypes.Matrix) {
					let firstQuestion = data.filter(m => m.Widget_ID === w.Widget_ID).reduce((prev, curr) => prev.Question_ID < curr.Question_ID ? prev : curr);
					let matrixQuestions = data.filter(m => (m.Widget_ID === w.Widget_ID && m.Question_ID !== firstQuestion.Question_ID))
						.reduce((result, q) => {
							if (!result.some(r => r.Question_ID === q.Question_ID)) {
								result.push({
									Question_ID: q.Question_ID,
									Question_Type_ID: q.Question_Type_ID,
									Question_Text: stripTags(q.Question_Text)
								})
							}
							return result;
						}, []);
					
					questionWidget = {
						Widget_ID: w.Widget_ID,
						Question_ID: firstQuestion.Question_ID,
						Question_Type_ID: firstQuestion.Question_Type_ID,
						Question_Text: stripTags(firstQuestion.Question_Text),
						Matrix_Questions: matrixQuestions
					}
				}
				else {
					questionWidget = {
						Widget_ID: w.Widget_ID,
						Question_ID: w.Question_ID,
						Question_Type_ID: w.Question_Type_ID,
						Question_Text: stripTags(w.Question_Text)
					}
				}
				updatedQuestionWidgets.push(questionWidget);
			}

			if(!updatedQuestionAnswers.has(w.Question_ID)) {
				updatedQuestionAnswers.set(w.Question_ID, []);
			}
			updatedQuestionAnswers.get(w.Question_ID).push({Answer_ID: w.Answer_ID, Answer_Text: w.Answer_Text ? stripTags(w.Answer_Text) : null});
		});
		setQuestionWidgets(updatedQuestionWidgets);
		setQuestionAnswers(updatedQuestionAnswers);

		// get lists that are involved in a condition
		let updatedLists = new Map();
		for (const rule of rulesData) {
			for (const condition of rule.Conditions) {
				if (condition.Question_Type_ID === QuestionTypes.Picklist) {
					let List_ID = parseInt(updatedQuestionAnswers.get(condition.Question_ID)[0].Answer_Text);
					if(!updatedLists.has(List_ID)) {
						let { data: List_Values } = await getListValues(List_ID);
						updatedLists.set(List_ID, List_Values)
					}
				}
			}
		}
		setLists(updatedLists);

		setLoading(false);
	}

	const addRule = () => {
		setRules([...rules, defaultRule]);
	};

	const removeRule = (idx) => {
		const updatedRules = [...rules];
		updatedRules.splice(idx, 1);
		setRules(updatedRules);
	};

	const addCondition = (ruleIdx) => {
		const updatedRules = [...rules];
    	updatedRules[ruleIdx].Conditions.push(defaultCondition);
    	setRules(updatedRules);
	};

	const removeCondition = (ruleIdx, conditionIdx) => {
		const updatedRules = [...rules];
		updatedRules[ruleIdx].Conditions.splice(conditionIdx, 1);
		setRules(updatedRules);
	};

	const handleRuleChange = (ruleIndex, name, value) => {
		const updatedRules = [...rules];
		if (name === "ruleAction") {
			updatedRules[ruleIndex].RuleAction = value;
		}
		else if (name === "ruleWidgetID") {
			updatedRules[ruleIndex].Widget_ID = parseInt(value);
		}
		else if (name === "ruleOperator") {
			updatedRules[ruleIndex].RuleOperator = value;
		}
		else if (name === "ruleAllowSaveOption") {
			updatedRules[ruleIndex].AllowSave = value === '-1' ? -1 : value === 'true';
		}
		else if (name === "ruleAlertMessage") {
			updatedRules[ruleIndex].AlertMessage = value;
		}
		setRules(updatedRules);
	}

	const handleConditionChange = async (ruleIndex, conditionIndex, name, value, questionID = null, matrixWidgetID = null) => {
		const updatedRules = [...rules];
		if (name === "conditionWidgetID") {
			let Widget_ID = parseInt(value);
			let questionWidget = questionWidgets.find(w => w.Widget_ID === Widget_ID);
			let condition = defaultCondition;
			condition.Widget_ID = Widget_ID;
			condition.Question_Type_ID = questionWidget.Question_Type_ID;
			condition.Question_ID = questionWidget.Question_ID;
			condition.Question_Text = questionWidget.Question_Text;
			if (questionWidget.Question_Type_ID === QuestionTypes.Checkbox) {
				condition.ConditionOperator = 'is Checked';
			}
			else if ([
				QuestionTypes.Multiple_Choice,
				QuestionTypes.Dropdown,
				QuestionTypes.Picklist,
				QuestionTypes.Institution,
				QuestionTypes.Scale,
				QuestionTypes.Matrix
			].includes(condition.Question_Type_ID)) {
				condition.ConditionOperator = 'is Selected';
			}

			// Picklist selection - get list values, Answer_ID will be List_Value_ID
			if (questionWidget.Question_Type_ID === QuestionTypes.Picklist) {
				let List_ID = parseInt(questionAnswers.get(questionWidget.Question_ID)[0].Answer_Text);
				condition.List_ID = List_ID;
				let updatedLists = new Map(lists);
				if(!updatedLists.has(List_ID)) {
					let { data: List_Values } = await getListValues(List_ID);
					updatedLists.set(List_ID, List_Values);
				}
				setLists(updatedLists);
			}

			updatedRules[ruleIndex].Conditions[conditionIndex] = condition;
		}
		else if (name === "conditionMatrixQuestionID" && matrixWidgetID) {
			let questionID = parseInt(value);
			let matrixQuestion = questionWidgets.find(w => w.Widget_ID === matrixWidgetID).Matrix_Questions.find(q => q.Question_ID === questionID);
			updatedRules[ruleIndex].Conditions[conditionIndex].Matrix_Question_ID = questionID;
			updatedRules[ruleIndex].Conditions[conditionIndex].Matrix_Question_Type_ID = matrixQuestion.Question_Type_ID;
			updatedRules[ruleIndex].Conditions[conditionIndex].Matrix_Question_Text = matrixQuestion.Question_Text;
		}
		else if (name === "conditionOperator") {
			updatedRules[ruleIndex].Conditions[conditionIndex].ConditionOperator = value;
		}
		else if (name === "conditionAnswerID" && questionID) { // Multiple Choice, Dropdown, Picklist, Institution, Scale, Matrix(Not Numeric Tally) - can have multiple answers per question
			let answerID = parseInt(value);
			updatedRules[ruleIndex].Conditions[conditionIndex].Answer_ID = answerID

			// update Answer_Text - needed for multi selection
			const condition = updatedRules[ruleIndex].Conditions[conditionIndex];
			if(condition.Question_Type_ID === QuestionTypes.Picklist) {
				updatedRules[ruleIndex].Conditions[conditionIndex].Answer_Text =
					lists.get(condition.List_ID).find(v => v.List_Value_ID === answerID).Value_Text;
			}
			else if (condition.Question_Type_ID === QuestionTypes.Institution) {
				updatedRules[ruleIndex].Conditions[conditionIndex].Answer_Text = institutions.find(i => i.Institution_ID === answerID).Institution_Name;
			}
			else {
				updatedRules[ruleIndex].Conditions[conditionIndex].Answer_Text =
					questionAnswers.get(questionID).find(a => a.Answer_ID === answerID).Answer_Text;
			}
		}
		else if (name === "conditionAnswerText") { // Text, Numeric, Date, Time, Numeric Tally - has no answer id
			updatedRules[ruleIndex].Conditions[conditionIndex].Answer_Text = value;
		}
		else if (name === "conditionMatrixAnswerText" && questionID) { // Matrix - Numeric Tally
			// to implement - low priority
		} else if (name === "conditionTargetWidgetID") {
			updatedRules[ruleIndex].Conditions[conditionIndex].Target_Widget_ID = parseInt(value)
		} else if (name === "compareWithWidget") {
			updatedRules[ruleIndex].Conditions[conditionIndex].CompareWidget = !updatedRules[ruleIndex].Conditions[conditionIndex].CompareWidget
		} else if (name === "checkRandomization") {
			updatedRules[ruleIndex].Conditions[conditionIndex].CheckIfRandomized = !updatedRules[ruleIndex].Conditions[conditionIndex].CheckIfRandomized
		} else if (name === "conditionRandomizationStatus") {
			updatedRules[ruleIndex].Conditions[conditionIndex].IsRandomized = parseInt(value)
			updatedRules[ruleIndex].Conditions[conditionIndex].Widget_ID = updatedRules[ruleIndex].Widget_ID
		}
		setRules(updatedRules);
	}

	/**
	 * Check rules have fields filled before save
	 * 1. check each rule has Widget ID
	 * 2. For each condition, check for:
	 *    Widget ID, Question ID, Answer ID/Answer Text, Matrix Question ID (if matrix)
	 */
	const verifyRules = () => {
		for (const rule of rules) {
			if((rule.RuleAction !== 'Alert' && (!rule.Widget_ID || rule.Widget_ID === 0)) || (rule.RuleAction ===  'Alert' && typeof rule.AllowSave !== 'boolean' )|| rule.Conditions.length === 0) {
				return false;
			}
			for (const condition of rule.Conditions) {
				console.log(condition.IsRandomized)
				if (condition.CheckIfRandomized && condition.IsRandomized === -1) {
					return false
				}

				if (!condition.CheckIfRandomized) {
					if (!condition.Widget_ID || condition.Widget_ID === 0 ||
						!condition.Question_Type_ID || condition.Question_Type_ID === 0 ||
						!condition.Question_ID || condition.Question_ID === 0 ||
						!condition.Question_Text) {
						return false;
					}

					if ([
						QuestionTypes.Multiple_Choice,
						QuestionTypes.Dropdown,
						QuestionTypes.Picklist,
						QuestionTypes.Institution,
						QuestionTypes.Scale,
						QuestionTypes.Matrix
					].includes(condition.Question_Type_ID)) {
						if (!condition.Answer_ID || condition.Answer_ID === 0) {
							return false;
						}
					}

					if(condition.Question_Type_ID === QuestionTypes.Matrix && (
						!condition.Matrix_Question_ID || condition.Matrix_Question_ID === 0 ||
						!condition.Matrix_Question_Type_ID || condition.Matrix_Question_Type_ID === 0)
					) {
						return false;
					}
				}
			}
		}
		return true;
	}

	const handleSave = async () => {
		if (!verifyRules()) {
			toaster('error', 'Please make sure all fields are filled')
			return;
		}

		let { data } = await saveFormClientSideValidation(Form_ID, rules);
        if (data.includes('Success')) {
            toaster('success', 'Form Client Side Validation Rules Saved')
            setOpenModal(false);
        }
        else {
            toaster('error', 'Form Client Side Validation Rules Save Failed')
        }
		
	};

	return (
		<>
			<button className="btn btn-primary" onClick={handleOpenModal}>
				<RouteRoundedIcon/>
			</button>

			<Modal size='xxl' show={openModal} onHide={() => setOpenModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Show/Hide Logic</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{loading && <CustomLoader/>}
					{!loading && rules.map((rule, ruleIndex) => (
						<div key={ruleIndex} className="mx-auto" style={{maxWidth: '1300px'}}>
							<label className="text-primary mt-2 pr-2">Rule #{ruleIndex + 1}</label>
							<button className='btn btn-outline-danger btn-icon btn-sm float-right' onClick={() => removeRule(ruleIndex)}>
								<Icon.Minus size={18} />
							</button>
							<div key={ruleIndex} className="border rounded p-3 mb-3" style={{backgroundColor: 'rgba(31, 45, 65, 0.03)'}}>
								<div className="row form-group justify-content-start">
									<div className="col-1">
										<select
											className="form-control form-control-sm"
											value={rule.RuleAction}
											name="ruleAction"
											onChange={(e) => {handleRuleChange(ruleIndex, e.target.name, e.target.value)}}
										>
											<option key={0} value='Show'>Show</option>
											<option key={1} value='Hide'>Hide</option>
											<option key={2} value='Clear'>Clear</option>
											<option key={3} value='Alert'>Alert</option>
										</select>
									</div>

									{rule.RuleAction !== 'Alert' && <div className="col-4">
										<select
											className="form-control form-control-sm"
											value={rule.Widget_ID}
											name="ruleWidgetID"
												onChange={(e) => handleRuleChange(ruleIndex, e.target.name, e.target.value)}
										>
											<option key={0} value={0}>Select Widget...</option>
											{rule.RuleAction !== 'Clear' ? 
											widgets.map((w) => (
												<option key={w.Widget_ID} value={w.Widget_ID}>
													Widget #{w.Widget_ID}: {w.Question_Text ? w.Question_Text : w.Widget_Name}
												</option>
											)) : questionWidgets.map((w) => (
												<option key={w.Widget_ID} value={w.Widget_ID}>
													Widget #{w.Widget_ID}: {w.Question_Text}
												</option>
											))}
										</select>
									</div>}

									{rule.RuleAction === 'Alert' && <>
									<div className='col-4'>
										<input
											type="text"
											className="form-control form-control-sm"
											value={rule.AlertMessage}
											name="ruleAlertMessage"
											placeholder={"Enter alert message to display"}
											onChange={(e) => handleRuleChange(ruleIndex, e.target.name, e.target.value)}
										/>
									</div>
									<div className="col-3">
										<select
											className="form-control form-control-sm"
											value={rule.AllowSave}
											name="ruleAllowSaveOption"
											onChange={(e) => handleRuleChange(ruleIndex, e.target.name, e.target.value)}
										>
											<option key={0} value={-1}>Select...</option>
											<option key={1} value={false}>Prevent saving responses</option>
											<option key={2} value={true}>Allow saving responses</option>
										</select>
									</div></>}

									{(rule.Widget_ID !== 0 || rule.RuleAction === 'Alert') && <div className="col-4 align-self-center"> if the following conditions are met:</div>}
								</div>
								{rule.Conditions.length === 0 && addCondition(ruleIndex)} 
								{(rule.Widget_ID !== 0 || rule.RuleAction === 'Alert') && rule.Conditions.map((condition, conditionIndex) => (
									<div>
									<div className="ml-15">
										<input type='checkbox' className='form-check-input' id='checkRandomization' name='checkRandomization' 
										checked={condition.CheckIfRandomized} onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)} />
										<label htmlFor='checkRandomization' className='form-check-label'>
											Check if randomized?
										</label>
									</div>
									{condition.CheckIfRandomized && <div className="row form-group justify-content-start" key={conditionIndex}>
										<div className= "col-1">
											{conditionIndex > 0 &&
												<select
													className="form-control form-control-sm"
													value={rule.RuleOperator}
													name="ruleOperator"
													onChange={(e) => handleRuleChange(ruleIndex, e.target.name, e.target.value)}
												>
													<option key={0} value='And'>And</option>
													<option key={1} value='Or'>Or</option>
												</select>
											}
												
										</div>
										<div className="col-4">
											<select
												className="form-control form-control-sm"
												value={condition.IsRandomized}
												name="conditionRandomizationStatus"
												onChange={(e) => {handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}}
											>
												<option key={-1} value={-1}>Select Randomization Status...</option>
												<option key={1} value={1}>
													Randomized
												</option>
												<option key={0} value={0}>
													Not Randomized
												</option>
											</select>
										</div>
										<div className="col-1">
											<button className="border-0 text-danger m-1" onClick={() => removeCondition(ruleIndex, conditionIndex)}>
												<Icon.MinusCircle size={18} />
											</button>
											<button className="border-0 text-success m-1" onClick={() => addCondition(ruleIndex)}>
												<Icon.PlusCircle size={18} />
											</button>
										</div>
										</div>}
									{!condition.CheckIfRandomized && <div className="row form-group justify-content-start" key={conditionIndex}>
										<div className= "col-1">
											{conditionIndex > 0 &&
												<select
													className="form-control form-control-sm"
													value={rule.RuleOperator}
													name="ruleOperator"
													onChange={(e) => handleRuleChange(ruleIndex, e.target.name, e.target.value)}
												>
													<option key={0} value='And'>And</option>
													<option key={1} value='Or'>Or</option>
												</select>
											}
												
										</div>
										<div className="col-4">
											<select
												className="form-control form-control-sm"
												value={condition.Widget_ID}
												name="conditionWidgetID"
												onChange={(e) => {handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}}
											>
												<option key={0} value={0}>Select Widget Question...</option>
												{questionWidgets.map((w) => (
													<option key={w.Widget_ID} value={w.Widget_ID}>
														Widget #{w.Widget_ID}: {w.Question_Text}
													</option>
												))}
											</select>
										</div>
										{condition.Widget_ID !== 0 && <>
											{condition.Question_Type_ID === QuestionTypes.Checkbox &&
												<div className="col-2">
													<select
														className="form-control form-control-sm"
														value={condition.ConditionOperator}
														name="conditionOperator"
														onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}
													>
														<option key={0} value='is Checked'>is Checked</option>
														<option key={1} value='is Not Checked'>is Not Checked</option>
													</select>
												</div>
											}
											{[
												QuestionTypes.Multiple_Choice,
												QuestionTypes.Dropdown,
												QuestionTypes.Picklist,
												QuestionTypes.Institution,
												QuestionTypes.Scale
											].includes(condition.Question_Type_ID) &&
												<>
													<div className="col-2">
														<select
															className="form-control form-control-sm"
															value={condition.Answer_ID}
															name="conditionAnswerID"
															onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value, condition.Question_ID)}
														>
															<option key={0} value={0}>Select Answer...</option>
															{condition.Question_Type_ID === QuestionTypes.Picklist &&
																lists.has(condition.List_ID) &&
																	lists.get(condition.List_ID).map(value => (
																		<option key={value.List_Value_ID} value={value.List_Value_ID}>
																			{value.Value_Text}
																		</option>
																	))
															}
															{condition.Question_Type_ID === QuestionTypes.Institution &&
																institutions.map(i => (
																	<option key={i.Institution_ID} value={i.Institution_ID}>
																		{i.Institution_Name}
																	</option>
																))
															}
															{[
																QuestionTypes.Multiple_Choice,
																QuestionTypes.Dropdown,
																QuestionTypes.Scale
															].includes(condition.Question_Type_ID) &&
																questionAnswers.has(condition.Question_ID) &&
																	questionAnswers.get(condition.Question_ID).map((answer) => (
																		<option key={answer.Answer_ID} value={answer.Answer_ID}>
																			{answer.Answer_Text}
																		</option>
																))
															}
														</select>
													</div>
												</>
											}
											{[
												QuestionTypes.Text,
												QuestionTypes.Numeric,
												QuestionTypes.Numeric_Tally,
												QuestionTypes.Date,
												QuestionTypes.Time,
												QuestionTypes.Textarea

											].includes(condition.Question_Type_ID) &&
												<>
													<div className="col-2">
														<select
															className="form-control form-control-sm"
															value={condition.ConditionOperator}
															name="conditionOperator"
															onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}
														>
															<option key={0} value='Equals'>Equals</option>
															<option key={1} value='is Empty'>is Empty</option>
															<option key={2} value='is Not Empty'>is Not Empty</option>
															{[ 
																QuestionTypes.Numeric,
																QuestionTypes.Numeric_Tally,
																QuestionTypes.Date,
																QuestionTypes.Time
															].includes(condition.Question_Type_ID) && <>
															<option key={3} value='Less Than'>Less Than</option>
															<option key={4} value='Larger Than'>Larger Than</option></>}
														</select>
													</div>
													{['Equals', 'Less Than', 'Larger Than'].includes(condition.ConditionOperator) &&
														<>
															<div className="col-1">
																<input
																	type="checkbox"
																	checked={condition.CompareWidget}
																	value={condition.CompareWidget}
																	onChange={(e) => {handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}}
																	name="compareWithWidget"
																	id="compareWithWidget"
																/>
																<label htmlFor='compareWithWidget' className="ml-1"
																>
																	Widget?
																</label>
															</div>
															<div className='col-3'>
																{!condition.CompareWidget && <input
																	type="text"
																	className="form-control form-control-sm"
																	value={condition.Answer_Text}
																	name="conditionAnswerText"
																	placeholder={condition.Question_Type_ID === QuestionTypes.Date ? "yyyy-mm-dd" :
																		(condition.Question_Type_ID === QuestionTypes.Time ? "HH:mm (24 hr)" : "")}
																	onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}
																/>}
																{condition.CompareWidget &&<select
																	className="form-control form-control-sm"
																	value={condition.Target_Widget_ID}
																	name="conditionTargetWidgetID"
																	onChange={(e) => {handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}}
																>
																	<option key={0} value={0}>Select Widget Question...</option>
																	{questionWidgets.map((w) => (
																		<option key={w.Widget_ID} value={w.Widget_ID}>
																			Widget #{w.Widget_ID}: {w.Question_Text}
																		</option>
																	))}
																</select>}
															</div>
														</>
													}
												</>
											}
											{condition.Question_Type_ID === QuestionTypes.Matrix &&
												<>
													<div className="col-2">
														<select
															className="form-control form-control-sm"
															value={condition.Matrix_Question_ID}
															name="conditionMatrixQuestionID"
															onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value, null, condition.Widget_ID)}
														>
															<option key={0} value={0}>Select Matrix Question...</option>
															{questionWidgets.find(w => w.Widget_ID === condition.Widget_ID).Matrix_Questions.map((q) => (
																<option key={q.Question_ID} value={q.Question_ID}>
																	Question #{q.Question_ID}: {q.Question_Text}
																</option>
															))}
														</select>

													</div>
													{(condition.Matrix_Question_ID && condition.Matrix_Question_ID !== 0 &&
													 	condition.Matrix_Question_Type_ID !== QuestionTypes.Numeric_Tally) &&
														<>
														<div className="col-2">
															<select
																className="form-control form-control-sm"
																value={condition.Answer_ID}
																name="conditionAnswerID"
																onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value, condition.Matrix_Question_ID)}
															>
																<option key={0} value={0}>Select Answer...</option>
																{questionAnswers.has(condition.Matrix_Question_ID) &&
																	questionAnswers.get(condition.Matrix_Question_ID).map((answer) => (
																		<option key={answer.Answer_ID} value={answer.Answer_ID}>
																			{answer.Answer_Text}
																		</option>
																))}
															</select>
														</div>
														</>
													}
												</>
											}
											{[
													QuestionTypes.Multiple_Choice,
													QuestionTypes.Dropdown,
													QuestionTypes.Picklist,
													QuestionTypes.Institution,
													QuestionTypes.Scale,
													QuestionTypes.Matrix
												].includes(condition.Question_Type_ID) &&
												<div className="col-2">
													<select
														className="form-control form-control-sm"
														value={condition.ConditionOperator}
														name="conditionOperator"
														onChange={(e) => handleConditionChange(ruleIndex, conditionIndex, e.target.name, e.target.value)}
													>
														<option key={0} value='is Selected'>is Selected</option>
														<option key={1} value='is Not Selected'>is Not Selected</option>
													</select>
												</div>
											}
											</>
										}
										<div className="col-1">
											<button className="border-0 text-danger m-1" onClick={() => removeCondition(ruleIndex, conditionIndex)}>
												<Icon.MinusCircle size={18} />
											</button>
											<button className="border-0 text-success m-1" onClick={() => addCondition(ruleIndex)}>
												<Icon.PlusCircle size={18} />
											</button>
										</div>
										
									</div>}
									</div>
								))}
							</div>
						</div>
					))}
					<div className="row justify-content-center">
						<button className="btn btn-outline-primary" onClick={addRule}>
							+ Add Rule
						</button>
					</div>
				</Modal.Body>

				<Modal.Footer>
					<button className="btn btn-primary" onClick={handleSave}>
						Save
					</button>
				</Modal.Footer>
		</Modal>
		</>
	);
}

export default ClientSideValidation;
